/**
 * Zustand store для аутентификации и персонального bootstrap-состояния.
 */
import { create } from "zustand"
import { authApi, autoclanApi } from "../api/endpoints"
import { setAuthToken } from "../api/client"
import type { Autoclan, AutoclanDetail, User } from "../types/api"
import type { AppLocale } from "../i18n"
import { PLANNED_LANGUAGES } from "../i18n/languages"
import { isSpecialLocale } from "../i18n/specialLocales"
import { useLocaleStore } from "./localeStore"

function isRuntimeLocale(value: string | null | undefined): value is AppLocale {
  if (!value) return false
  return isSpecialLocale(value) || PLANNED_LANGUAGES.some((language) => language.code === value)
}

interface AuthState {
  user: User | null
  isLoading: boolean
  isAuthenticated: boolean
  error: string | null
  autoclans: Autoclan[]
  autoclanDetail: AutoclanDetail | null
  autoclanReady: boolean

  login: (initData: string, launchId: string) => Promise<void>
  logout: () => void
  fetchMe: () => Promise<void>
  refreshAutoclanState: () => Promise<void>
  heartbeat: () => Promise<void>
}

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  isLoading: false,
  // A persisted JWT is only a cache. It must never make the UI authenticated
  // before the current Telegram initData has been confirmed by the backend.
  isAuthenticated: false,
  error: null,
  autoclans: [],
  autoclanDetail: null,
  autoclanReady: false,

  login: async (initData: string, launchId: string) => {
    set({
      isLoading: true,
      error: null,
      isAuthenticated: false,
      user: null,
      autoclans: [],
      autoclanDetail: null,
      autoclanReady: false,
    })
    try {
      const response = await authApi.loginTelegram(initData, launchId)
      setAuthToken(response.access_token)
      await get().fetchMe()
      // Resolve the current user's clan before the global loading screen is removed.
      // AutoclanPage must never first paint the "create clan" state and then morph
      // into an already existing clan after its own mount request finishes.
      await get().refreshAutoclanState()
    } catch (err: any) {
      setAuthToken(null)
      useLocaleStore.getState().setLocale("en")
      set({
        user: null,
        error: err.message || "Ошибка авторизации",
        isAuthenticated: false,
        autoclans: [],
        autoclanDetail: null,
        autoclanReady: false,
      })
      throw err
    } finally {
      set({ isLoading: false })
    }
  },

  logout: () => {
    setAuthToken(null)
    useLocaleStore.getState().setLocale("en")
    set({
      user: null,
      isAuthenticated: false,
      autoclans: [],
      autoclanDetail: null,
      autoclanReady: false,
    })
  },

  fetchMe: async () => {
    try {
      const user = await authApi.getMe()
      const resolvedLanguageCode = user.app_language_code || user.suggested_language_code
      // The backend resolves: explicit BeepApp choice -> Telegram interface
      // language -> WebView/browser language -> GeoIP country -> English.
      // The detected country is only a language fallback and never selects a vehicle market.
      useLocaleStore.getState().setLocale(
        isRuntimeLocale(resolvedLanguageCode) ? resolvedLanguageCode : "en",
      )
      set({ user, isAuthenticated: true })
    } catch (err) {
      set({ user: null, isAuthenticated: false })
      setAuthToken(null)
      useLocaleStore.getState().setLocale("en")
      throw err
    }
  },

  refreshAutoclanState: async () => {
    try {
      const autoclans = await autoclanApi.list()
      const primaryClan = autoclans[0] || null
      const autoclanDetail = primaryClan ? await autoclanApi.detail(primaryClan.id) : null
      set({ autoclans, autoclanDetail, autoclanReady: true })
    } catch {
      // No active Garage / no Autoclan is a valid resolved personal state.
      set({ autoclans: [], autoclanDetail: null, autoclanReady: true })
    }
  },

  heartbeat: async () => {
    try {
      await authApi.heartbeat()
    } catch {
      // Игнорировать ошибки heartbeat
    }
  },
}))
